import type {
  Dashboard,
  DashboardList,
  DashboardTile,
  AccountState,
  AdapterInfo,
  CapabilityDefinition,
  ConnectionStatus,
  ConnectionView,
  CredentialOption,
  CredentialsState,
  DeviceBinding,
  DeviceDetail,
  DeviceInfo,
  DeviceTemplate,
  DiscoveredDevice,
  EnergyReport,
  EnergyResolution,
  LatestValue,
  ExtensionInfo,
  GeoPlace,
  MeasurementPoint,
  NodeInput,
  PointBinding,
  PointReading,
  Annotation,
  FloorPlan,
  PlanMark,
  PlanMap,
  Opportunity,
  BillingPoint,
  Budget,
  BudgetInput,
  BudgetStatus,
  FinanceCosts,
  Invoice,
  InvoiceCheck,
  InvoiceInput,
  MonthCost,
  TariffInput,
  TariffVersion,
  PlanOrientation,
  ReactiveReport,
  ReportChange,
  ReportPreview,
  ReportDefinition,
  ReportInput,
  ReportSnapshot,
  ReportTemplate,
  CommissioningItem,
  CommissioningView,
  PlatformEvent,
  EventLogEntry,
  TaskStage,
  AlertRule,
  RuleInput,
  RulePreview,
  TenantUser,
  AppNotification,
  SupplyLink,
  FlowReport,
  CostAllocation,
  UtilityReport,
  PowerNow,
  SeriesPoint,
  SeriesResolution,
  SessionUser,
  SwitchResult,
  EvCharger,
  FlexResource,
  AccessHistoryEntry,
  InvoiceDraft,
  ReactiveBucket,
  ClusterOrder,
  OrderCheck,
  ClusterMemberView,
  Cluster,
  ClusterMember,
  ClusterReport,
  MarketPrice,
  AutomationDecision,
  AutomationInput,
  AutomationRule,
  SwitchTarget,
  AccessGrant,
  GrantRole,
  UserPreferences,
  SiteNode,
  SiteTree,
  UnitCatalog,
} from "./types.ts";

const id = encodeURIComponent;

export interface ApiErrorItem {
  code: string;
  params: Record<string, unknown>;
}

/** Errors carry codes and parameters only; the UI turns them into text through translations. */
export class ApiError extends Error {
  readonly status: number;
  readonly errors: ApiErrorItem[];

  constructor(status: number, errors: ApiErrorItem[]) {
    super(errors[0]?.code ?? `HTTP_${status}`);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }
}

interface CachedEntry {
  etag: string;
  data: unknown;
}

/**
 * The host rejects state-changing API calls without this header. Browsers cannot add it to cross-site
 * requests without a CORS preflight, which the host never allows, so it protects the session cookie from CSRF.
 */
export const CLIENT_HEADER = { "X-EP-Client": "web" } as const;

export interface ApiClientOptions {
  baseUrl?: string;
  fetch?: typeof fetch;
  /** Called on every 401, e.g. to show the sign-in screen when the session has expired. */
  onUnauthorized?: () => void;
}

/**
 * Thin client for /api/v1. GET responses with an ETag are kept in memory and revalidated with
 * If-None-Match, so rarely changing resources cost one 304 instead of a full download.
 * Concurrent GETs of the same URL share one request. The session is a cookie; no token is handled here.
 */
export class ApiClient {
  private readonly cache = new Map<string, CachedEntry>();
  private readonly inFlight = new Map<string, Promise<unknown>>();
  private readonly fetcher: typeof fetch;
  private readonly baseUrl: string;
  private readonly onUnauthorized: (() => void) | undefined;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? "";
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.onUnauthorized = options.onUnauthorized;
  }

  readonly me = () => this.get<SessionUser>("/api/v1/me");

  /** Day-ahead market prices (TGE), the same for everyone. */
  readonly market = {
    prices: (from: Date, to: Date, resolution: "hour" | "quarter") =>
      this.get<MarketPrice[]>(
        `/api/v1/market/prices?${query({ from: from.toISOString(), to: to.toISOString(), resolution })}`,
      ),
  };

  /** Simple automations in test mode: rules, the switches they control and the log of their decisions. */
  readonly automations = {
    list: () => this.get<AutomationRule[]>("/api/v1/automations"),
    targets: () => this.get<SwitchTarget[]>("/api/v1/automations/targets"),
    decisions: (ruleId?: string) =>
      this.get<AutomationDecision[]>(
        `/api/v1/automations/decisions${ruleId ? `?${query({ rule: ruleId })}` : ""}`,
      ),
    save: (ruleId: string | null, input: AutomationInput) =>
      ruleId
        ? this.send<AutomationRule>(
            "PUT",
            `/api/v1/automations/${id(ruleId)}`,
            input,
          )
        : this.send<AutomationRule>("POST", "/api/v1/automations", input),
    remove: (ruleId: string) =>
      this.send<void>("DELETE", `/api/v1/automations/${id(ruleId)}`),
    switchNow: (deviceId: string, channel: string, on: boolean) =>
      this.send<SwitchResult>("POST", "/api/v1/automations/switch", {
        deviceId,
        channel,
        on,
      }),
  };

  /** Energy clusters computed on the platform's data. */
  readonly clusters = {
    list: () => this.get<Cluster[]>("/api/v1/clusters"),
    save: (clusterId: string | null, name: string) =>
      clusterId
        ? this.send<Cluster>("PUT", `/api/v1/clusters/${id(clusterId)}`, {
            name,
          })
        : this.send<Cluster>("POST", "/api/v1/clusters", { name }),
    remove: (clusterId: string) =>
      this.send<void>("DELETE", `/api/v1/clusters/${id(clusterId)}`),
    members: (clusterId: string) =>
      this.get<ClusterMember[]>(`/api/v1/clusters/${id(clusterId)}/members`),
    saveMember: (
      clusterId: string,
      memberId: string | null,
      input: {
        nodeId: string;
        weight: number;
        joinedOn: string;
        leftOn: string | null;
      },
    ) =>
      memberId
        ? this.send<ClusterMember>(
            "PUT",
            `/api/v1/clusters/${id(clusterId)}/members/${id(memberId)}`,
            input,
          )
        : this.send<ClusterMember>(
            "POST",
            `/api/v1/clusters/${id(clusterId)}/members`,
            input,
          ),
    removeMember: (clusterId: string, memberId: string) =>
      this.send<void>(
        "DELETE",
        `/api/v1/clusters/${id(clusterId)}/members/${id(memberId)}`,
      ),
    mine: (from: Date, to: Date) =>
      this.get<ClusterMemberView[]>(
        `/api/v1/clusters/mine?${query({ from: from.toISOString(), to: to.toISOString() })}`,
      ),
    flexibility: (clusterId: string) =>
      this.get<FlexResource[]>(`/api/v1/clusters/${id(clusterId)}/flexibility`),
    orders: (clusterId: string) =>
      this.get<ClusterOrder[]>(`/api/v1/clusters/${id(clusterId)}/orders`),
    createOrder: (
      clusterId: string,
      input: {
        start: string;
        end: string;
        goal: string;
        targetKw: number;
        deviceIds: string[];
        mode: string;
      },
    ) =>
      this.send<ClusterOrder>(
        "POST",
        `/api/v1/clusters/${id(clusterId)}/orders`,
        input,
      ),
    cancelOrder: (clusterId: string, orderId: string) =>
      this.send<ClusterOrder>(
        "POST",
        `/api/v1/clusters/${id(clusterId)}/orders/${id(orderId)}/cancel`,
        {},
      ),
    checkOrder: (clusterId: string, orderId: string) =>
      this.get<OrderCheck>(
        `/api/v1/clusters/${id(clusterId)}/orders/${id(orderId)}/check`,
      ),
    report: (clusterId: string, from: Date, to: Date) =>
      this.get<ClusterReport>(
        `/api/v1/clusters/${id(clusterId)}/report?${query({ from: from.toISOString(), to: to.toISOString() })}`,
      ),
  };

  /** EV charging: chargers and changes sent to them. */
  readonly charging = {
    chargers: () => this.get<EvCharger[]>("/api/v1/charging/chargers"),
    change: (
      deviceId: string,
      change: {
        mode?: string;
        maxCurrent?: number;
        phases?: string;
        limitSoc?: number;
      },
    ) =>
      this.send<SwitchResult>(
        "POST",
        `/api/v1/charging/chargers/${id(deviceId)}`,
        {
          mode: change.mode ?? null,
          maxCurrent: change.maxCurrent ?? null,
          phases: change.phases ?? null,
          limitSoc: change.limitSoc ?? null,
        },
      ),
  };

  /** Reads an invoice's PDF into a draft for the invoice form; nothing is stored. */
  async parseInvoicePdf(file: Blob): Promise<InvoiceDraft> {
    const response = await this.fetcher(
      `${this.baseUrl}/api/v1/finance/invoices/parse`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: { ...CLIENT_HEADER, "Content-Type": "application/pdf" },
        body: file,
      },
    );
    if (!response.ok) {
      throw await this.toError(response);
    }
    return (await response.json()) as InvoiceDraft;
  }

  /** Access to parts of the tree given in the platform. */
  readonly access = {
    roles: () => this.get<GrantRole[]>("/api/v1/access/roles"),
    grants: () => this.get<AccessGrant[]>("/api/v1/access/grants"),
    grant: (input: {
      userId: string | null;
      email: string | null;
      role: string;
      nodeId: string;
      validUntil: string | null;
    }) =>
      this.send<{
        id: string;
        directory:
          "invited" | "granted" | "hasRole" | "notConfigured" | "failed";
      }>("POST", "/api/v1/access/grants", input),
    revoke: (grantId: string) =>
      this.send<void>("DELETE", `/api/v1/access/grants/${id(grantId)}`),
    history: () => this.get<AccessHistoryEntry[]>("/api/v1/access/history"),
    at: (nodeId: string, at: Date) =>
      this.get<AccessGrant[]>(
        `/api/v1/access/at?${query({ node: nodeId, at: at.toISOString() })}`,
      ),
  };

  /** Dashboards: the overview made of tiles, one's own and the shared ones. */
  readonly dashboards = {
    list: () => this.get<DashboardList>("/api/v1/dashboards"),
    save: (
      dashboardId: string | null,
      input: { name: string; shared: boolean; tiles: DashboardTile[] },
    ) =>
      dashboardId
        ? this.send<Dashboard>(
            "PUT",
            `/api/v1/dashboards/${id(dashboardId)}`,
            input,
          )
        : this.post<Dashboard>("/api/v1/dashboards", input),
    remove: (dashboardId: string) =>
      this.send<void>("DELETE", `/api/v1/dashboards/${id(dashboardId)}`),
    setDefault: (dashboardId: string | null) =>
      this.send<void>("PUT", "/api/v1/dashboards/default", { dashboardId }),
    setOrganizationDefault: (dashboardId: string) =>
      this.send<void>(
        "PUT",
        `/api/v1/dashboards/${id(dashboardId)}/organization-default`,
      ),
  };

  /** The signed-in person's own settings. */
  readonly preferences = {
    get: () => this.get<UserPreferences>("/api/v1/me/preferences"),
    save: (preferences: UserPreferences) =>
      this.send<UserPreferences>("PUT", "/api/v1/me/preferences", preferences),
  };

  readonly catalog = {
    units: () => this.get<UnitCatalog>("/api/v1/catalog/units"),
    capabilities: () =>
      this.get<CapabilityDefinition[]>("/api/v1/catalog/capabilities"),
    deviceTemplates: () =>
      this.get<DeviceTemplate[]>("/api/v1/catalog/device-templates"),
  };

  readonly extensions = () =>
    this.get<{ extensions: ExtensionInfo[]; adapters: AdapterInfo[] }>(
      "/api/v1/extensions",
    );

  readonly devices = {
    list: () => this.get<DeviceInfo[]>("/api/v1/devices"),
    get: (deviceId: string) =>
      this.get<DeviceDetail>(`/api/v1/devices/${id(deviceId)}`),
    latest: (deviceId: string) =>
      this.get<LatestValue[]>(`/api/v1/devices/${id(deviceId)}/latest`),
    series: (
      deviceId: string,
      channel: string,
      attribute: string,
      from: Date,
      to: Date,
      resolution: SeriesResolution,
    ) =>
      this.get<SeriesPoint[]>(
        `/api/v1/devices/${id(deviceId)}/series?${query({ channel, attribute, from: from.toISOString(), to: to.toISOString(), resolution })}`,
      ),
    rename: (deviceId: string, name: string) =>
      this.send<DeviceInfo>("PATCH", `/api/v1/devices/${id(deviceId)}`, {
        name,
      }),
    /** Deletes the device with its measurement history. */
    remove: (deviceId: string) =>
      this.send<void>("DELETE", `/api/v1/devices/${id(deviceId)}`),
  };

  readonly geo = {
    /** Address search (external provider); call on an explicit user action, never while typing. */
    search: (q: string, lang: string) =>
      this.get<GeoPlace[]>(`/api/v1/geo/search?${query({ q, lang })}`),
    /** How the aerial photo reaches the browser: kept by the platform, or straight from the geoportal. */
    basemaps: () =>
      this.get<{ orthophoto: "platform" | "browser" }>("/api/v1/geo/basemaps"),
  };

  readonly sites = {
    tree: () => this.get<SiteTree>("/api/v1/nodes/tree"),
    createNode: (input: NodeInput) =>
      this.post<SiteNode>("/api/v1/nodes", input),
    updateNode: (nodeId: string, input: NodeInput) =>
      this.send<SiteNode>("PUT", `/api/v1/nodes/${id(nodeId)}`, input),
    removeNode: (nodeId: string) =>
      this.send<void>("DELETE", `/api/v1/nodes/${id(nodeId)}`),
    energy: (
      nodeId: string,
      from: Date,
      to: Date,
      resolution: EnergyResolution,
    ) =>
      this.get<EnergyReport>(
        `/api/v1/nodes/${id(nodeId)}/energy?${query({ from: from.toISOString(), to: to.toISOString(), resolution })}`,
      ),
    reactiveSeries: (
      nodeId: string,
      from: Date,
      to: Date,
      resolution: EnergyResolution,
    ) =>
      this.get<ReactiveBucket[]>(
        `/api/v1/nodes/${id(nodeId)}/reactive/series?${query({ from: from.toISOString(), to: to.toISOString(), resolution })}`,
      ),
    reactive: (nodeId: string, from: Date, to: Date) =>
      this.get<ReactiveReport>(
        `/api/v1/nodes/${id(nodeId)}/reactive?${query({ from: from.toISOString(), to: to.toISOString() })}`,
      ),
    flows: (
      nodeId: string,
      from: Date,
      to: Date,
      resolution: EnergyResolution,
    ) =>
      this.get<FlowReport>(
        `/api/v1/nodes/${id(nodeId)}/flows?${query({ from: from.toISOString(), to: to.toISOString(), resolution })}`,
      ),
    /** Supply links of one family of meters: electricity by default, or "water", "gas", "air". */
    supply: (nodeId: string, family?: string) =>
      this.get<SupplyLink[]>(
        `/api/v1/nodes/${id(nodeId)}/supply${family ? `?${query({ family })}` : ""}`,
      ),
    /** Volumes of the water, gas or compressed-air meters of the node and their balance. */
    utility: (
      nodeId: string,
      medium: string,
      from: Date,
      to: Date,
      resolution: EnergyResolution,
    ) =>
      this.get<UtilityReport>(
        `/api/v1/nodes/${id(nodeId)}/utility?${query({ medium, from: from.toISOString(), to: to.toISOString(), resolution })}`,
      ),
    setPulseWeight: (pointId: string, unitsPerPulse: number | null) =>
      this.send<void>("PUT", `/api/v1/points/${id(pointId)}/pulse-weight`, {
        unitsPerPulse,
      }),
    setSupply: (
      pointId: string,
      supply: {
        feedFrom: string | null;
        backupFrom: string | null;
        capacityKw: number | null;
      },
    ) => this.send<void>("PUT", `/api/v1/points/${id(pointId)}/supply`, supply),
    now: (nodeId: string) =>
      this.get<PowerNow>(`/api/v1/nodes/${id(nodeId)}/now`),
    /** The main value of every point in the node's subtree. */
    /**
     * Energy a point drew per bucket (bucket start, kWh), as the balance counts it; buckets without data are left out.
     */
    pointEnergy: (
      pointId: string,
      from: Date,
      to: Date,
      resolution: EnergyResolution,
    ) =>
      this.get<{ at: string; kwh: number }[]>(
        `/api/v1/points/${id(pointId)}/energy?${query({ from: from.toISOString(), to: to.toISOString(), resolution })}`,
      ),
    pointsNow: (nodeId: string) =>
      this.get<PointReading[]>(`/api/v1/nodes/${id(nodeId)}/points/now`),
    /** A new point; with `device` it is measured by that device channel from now on (saved together). */
    createPoint: (
      nodeId: string,
      name: string,
      medium: string,
      role: string,
      device?: { deviceId: string; channel: string; invert: boolean },
    ) =>
      this.post<MeasurementPoint>("/api/v1/points", {
        nodeId,
        name,
        medium,
        role,
        device: device ?? null,
      }),
    /** Several points in one node saved together (all or none), e.g. a device with every channel. */
    createPoints: (
      nodeId: string,
      points: {
        name: string;
        medium: string;
        role: string;
        device: { deviceId: string; channel: string; invert: boolean } | null;
      }[],
    ) =>
      this.post<MeasurementPoint[]>("/api/v1/points/batch", { nodeId, points }),
    updatePoint: (
      pointId: string,
      input: {
        nodeId: string;
        name: string;
        medium: string;
        role: string;
        readiness: string;
      },
    ) =>
      this.send<MeasurementPoint>(
        "PUT",
        `/api/v1/points/${id(pointId)}`,
        input,
      ),
    removePoint: (pointId: string) =>
      this.send<void>("DELETE", `/api/v1/points/${id(pointId)}`),
    pointBindings: (pointId: string) =>
      this.get<PointBinding[]>(`/api/v1/points/${id(pointId)}/bindings`),
    bindPoint: (
      pointId: string,
      deviceId: string,
      channel: string,
      invert: boolean,
      from: Date | null,
    ) =>
      this.post<PointBinding>(`/api/v1/points/${id(pointId)}/bindings`, {
        deviceId,
        channel,
        invert,
        from: from?.toISOString() ?? null,
      }),
    unbindPoint: (pointId: string) =>
      this.send<void>(
        "DELETE",
        `/api/v1/points/${id(pointId)}/bindings/current`,
      ),
    /** Counts all earlier data of the point's only channel (e.g. history an integration fetched after binding). */
    measureSinceFirstReading: (pointId: string) =>
      this.post<PointBinding>(
        `/api/v1/points/${id(pointId)}/bindings/since-first-reading`,
        {},
      ),
  };

  readonly events = {
    list: () => this.get<PlatformEvent[]>("/api/v1/events"),
    get: (eventId: string) =>
      this.get<{ event: PlatformEvent; log: EventLogEntry[] }>(
        `/api/v1/events/${id(eventId)}`,
      ),
    acknowledge: (ids: string[]) =>
      this.post<void>("/api/v1/events/acknowledge", { ids }),
    /** Changes the task of several events: stage, assignee (a user id), due date and a note. */
    task: (
      ids: string[],
      change: {
        stage?: TaskStage | null;
        assignee?: string | null;
        due?: string | null;
        note?: string | null;
      },
    ) =>
      this.post<PlatformEvent[]>("/api/v1/events/task", {
        ids,
        stage: change.stage ?? null,
        assignee: change.assignee ?? null,
        due: change.due ?? null,
        note: change.note ?? null,
      }),
    rules: () => this.get<AlertRule[]>("/api/v1/alert-rules"),
    createRule: (rule: RuleInput) =>
      this.post<AlertRule>("/api/v1/alert-rules", rule),
    updateRule: (ruleId: string, rule: RuleInput) =>
      this.send<AlertRule>("PUT", `/api/v1/alert-rules/${id(ruleId)}`, rule),
    previewRule: (rule: RuleInput) =>
      this.post<RulePreview>("/api/v1/alert-rules/preview", rule),
    muteRule: (ruleId: string, minutes: number) =>
      this.post<AlertRule>(`/api/v1/alert-rules/${id(ruleId)}/mute`, {
        minutes,
      }),
    removeRule: (ruleId: string) =>
      this.send<void>("DELETE", `/api/v1/alert-rules/${id(ruleId)}`),
    users: () => this.get<TenantUser[]>("/api/v1/users"),
    notifications: () => this.get<AppNotification[]>("/api/v1/notifications"),
    /** Marks the given notifications read, or all of them. */
    markRead: (ids: string[] | null) =>
      this.post<void>("/api/v1/notifications/read", { ids }),
  };

  readonly commissioning = {
    pending: (nodeId: string) =>
      this.get<CommissioningItem[]>(
        `/api/v1/nodes/${id(nodeId)}/commissioning`,
      ),
    view: (pointId: string) =>
      this.get<CommissioningView>(
        `/api/v1/points/${id(pointId)}/commissioning`,
      ),
    setCheck: (
      pointId: string,
      key: string,
      state: "ok" | "warn" | "na",
      note: string | null,
    ) =>
      this.send<void>(
        "PUT",
        `/api/v1/points/${id(pointId)}/commissioning/checks/${id(key)}`,
        { state, note },
      ),
    submit: (pointId: string, note: string | null) =>
      this.post<CommissioningView>(
        `/api/v1/points/${id(pointId)}/commissioning/submit`,
        { note },
      ),
    accept: (pointId: string, note: string | null) =>
      this.post<CommissioningView>(
        `/api/v1/points/${id(pointId)}/commissioning/accept`,
        { note },
      ),
    sendBack: (pointId: string, note: string | null) =>
      this.post<CommissioningView>(
        `/api/v1/points/${id(pointId)}/commissioning/return`,
        { note },
      ),
  };

  readonly plans = {
    /** The place's floor plan, or null when it has none. */
    get: async (nodeId: string): Promise<FloorPlan | null> => {
      const response = await this.fetcher(
        `${this.baseUrl}/api/v1/nodes/${id(nodeId)}/plan`,
        { credentials: "same-origin", headers: { ...CLIENT_HEADER } },
      );
      if (!response.ok) {
        throw await this.toError(response);
      }
      return response.status === 204
        ? null
        : ((await response.json()) as FloorPlan);
    },
    /** The plan image as a blob (it needs the same authentication as the API, so it cannot be a plain image URL). */
    image: async (nodeId: string, updatedAt?: string): Promise<Blob> => {
      // With the plan's version the browser keeps the image until the plan changes, without asking again.
      const version = updatedAt ? `?v=${Date.parse(updatedAt)}` : "";
      const response = await this.fetcher(
        `${this.baseUrl}/api/v1/nodes/${id(nodeId)}/plan/image${version}`,
        { credentials: "same-origin", headers: { ...CLIENT_HEADER } },
      );
      if (!response.ok) {
        throw await this.toError(response);
      }
      return response.blob();
    },
    setImage: async (nodeId: string, file: Blob): Promise<void> => {
      const response = await this.fetcher(
        `${this.baseUrl}/api/v1/nodes/${id(nodeId)}/plan/image`,
        {
          method: "PUT",
          credentials: "same-origin",
          headers: {
            ...CLIENT_HEADER,
            "Content-Type": file.type || "application/octet-stream",
          },
          body: file,
        },
      );
      if (!response.ok) {
        throw await this.toError(response);
      }
    },
    setMarks: (nodeId: string, marks: PlanMark[]) =>
      this.send<void>("PUT", `/api/v1/nodes/${id(nodeId)}/plan/marks`, {
        marks,
      }),
    /** Makes a map the plan's background; marks placed on an image are dropped. */
    setMap: (nodeId: string, map: PlanMap) =>
      this.send<void>("PUT", `/api/v1/nodes/${id(nodeId)}/plan/map`, map),
    setScale: (
      nodeId: string,
      widthM: number | null,
      orientation: PlanOrientation,
    ) =>
      this.send<void>("PUT", `/api/v1/nodes/${id(nodeId)}/plan/scale`, {
        widthM,
        orientation,
      }),
    remove: (nodeId: string) =>
      this.send<void>("DELETE", `/api/v1/nodes/${id(nodeId)}/plan`),
  };

  readonly optimization = {
    list: (nodeId: string) =>
      this.get<Opportunity[]>(`/api/v1/nodes/${id(nodeId)}/opportunities`),
    set: (
      nodeId: string,
      kind: string,
      input: { stage: string; owner: string | null; note: string | null },
    ) =>
      this.send<void>(
        "PUT",
        `/api/v1/nodes/${id(nodeId)}/opportunities/${id(kind)}`,
        input,
      ),
  };

  readonly finance = {
    /** Tariff groups with their zones and the zone of each hour (winter and summer workday, day off). */
    tariffGroups: () =>
      this.get<
        {
          key: string;
          medium: string;
          zones: string[];
          winter: string[];
          summer: string[];
          dayOff: string[];
          summerDayOff: string[];
        }[]
      >("/api/v1/finance/tariff-groups"),
    billingPoints: () =>
      this.get<BillingPoint[]>("/api/v1/finance/billing-points"),
    saveBillingPoint: (
      billingPointId: string | null,
      input: {
        name: string;
        pointId: string;
        identifier: string | null;
        operator: string | null;
        seller: string | null;
      },
    ) =>
      billingPointId
        ? this.send<BillingPoint>(
            "PUT",
            `/api/v1/finance/billing-points/${id(billingPointId)}`,
            input,
          )
        : this.post<BillingPoint>("/api/v1/finance/billing-points", input),
    removeBillingPoint: (billingPointId: string) =>
      this.send<void>(
        "DELETE",
        `/api/v1/finance/billing-points/${id(billingPointId)}`,
      ),
    versions: () =>
      this.get<TariffVersion[]>("/api/v1/finance/tariff-versions"),
    /** A new version from its date, or (with versionId) a correction of one entered wrongly. */
    saveVersion: (
      billingPointId: string,
      versionId: string | null,
      input: TariffInput,
    ) =>
      versionId
        ? this.send<TariffVersion>(
            "PUT",
            `/api/v1/finance/billing-points/${id(billingPointId)}/versions/${id(versionId)}`,
            input,
          )
        : this.post<TariffVersion>(
            `/api/v1/finance/billing-points/${id(billingPointId)}/versions`,
            input,
          ),
    removeVersion: (versionId: string) =>
      this.send<void>(
        "DELETE",
        `/api/v1/finance/tariff-versions/${id(versionId)}`,
      ),
    costs: (nodeId: string, from: Date, to: Date) =>
      this.get<FinanceCosts>(
        `/api/v1/finance/costs?${query({ node: nodeId, from: from.toISOString(), to: to.toISOString() })}`,
      ),
    /** The billing point's cost over the period split over the sub-meters it feeds. */
    allocation: (billingPointId: string, from: Date, to: Date) =>
      this.get<CostAllocation>(
        `/api/v1/finance/billing-points/${id(billingPointId)}/allocation?${query({ from: from.toISOString(), to: to.toISOString() })}`,
      ),
    monthly: (nodeId: string, year: number) =>
      this.get<MonthCost[]>(
        `/api/v1/finance/monthly?${query({ node: nodeId, year: String(year) })}`,
      ),
    budgets: () => this.get<BudgetStatus[]>("/api/v1/finance/budgets"),
    saveBudget: (budgetId: string | null, input: BudgetInput) =>
      budgetId
        ? this.send<Budget>(
            "PUT",
            `/api/v1/finance/budgets/${id(budgetId)}`,
            input,
          )
        : this.post<Budget>("/api/v1/finance/budgets", input),
    removeBudget: (budgetId: string) =>
      this.send<void>("DELETE", `/api/v1/finance/budgets/${id(budgetId)}`),
    invoices: () => this.get<InvoiceCheck[]>("/api/v1/finance/invoices"),
    saveInvoice: (invoiceId: string | null, input: InvoiceInput) =>
      invoiceId
        ? this.send<InvoiceCheck>(
            "PUT",
            `/api/v1/finance/invoices/${id(invoiceId)}`,
            input,
          )
        : this.post<InvoiceCheck>("/api/v1/finance/invoices", input),
    acceptInvoice: (invoiceId: string, explanation: string) =>
      this.post<Invoice>(`/api/v1/finance/invoices/${id(invoiceId)}/accept`, {
        explanation,
      }),
    removeInvoice: (invoiceId: string) =>
      this.send<void>("DELETE", `/api/v1/finance/invoices/${id(invoiceId)}`),
  };

  readonly reports = {
    templates: () => this.get<ReportTemplate[]>("/api/v1/report-templates"),
    list: () => this.get<ReportDefinition[]>("/api/v1/reports"),
    create: (input: ReportInput) =>
      this.post<ReportDefinition>("/api/v1/reports", input),
    update: (reportId: string, input: ReportInput) =>
      this.send<ReportDefinition>(
        "PUT",
        `/api/v1/reports/${id(reportId)}`,
        input,
      ),
    remove: (reportId: string) =>
      this.send<void>("DELETE", `/api/v1/reports/${id(reportId)}`),
    /** What the report would contain now, without storing anything. */
    preview: (input: ReportInput) =>
      this.post<ReportPreview>("/api/v1/reports/preview", input),
    generate: (
      reportId: string,
      options: { from?: Date; until?: Date; comment?: string } = {},
    ) =>
      this.post<ReportSnapshot>(`/api/v1/reports/${id(reportId)}/generate`, {
        from: options.from?.toISOString() ?? null,
        until: options.until?.toISOString() ?? null,
        comment: options.comment ?? null,
      }),
    snapshots: () => this.get<ReportSnapshot[]>("/api/v1/report-snapshots"),
    snapshot: (snapshotId: string) =>
      this.get<ReportSnapshot>(`/api/v1/report-snapshots/${id(snapshotId)}`),
    /** The snapshot's figures next to the same figures from today's data. */
    changes: (snapshotId: string) =>
      this.get<ReportChange[]>(
        `/api/v1/report-snapshots/${id(snapshotId)}/changes`,
      ),
  };

  readonly annotations = {
    list: (pointIds: string[], from: Date, to: Date) =>
      this.get<Annotation[]>(
        `/api/v1/annotations?${query({ from: from.toISOString(), to: to.toISOString(), points: pointIds.join(",") })}`,
      ),
    add: (input: {
      at: Date;
      text: string;
      pointIds: string[];
      private: boolean;
    }) =>
      this.post<Annotation>("/api/v1/annotations", {
        at: input.at.toISOString(),
        text: input.text,
        pointIds: input.pointIds,
        private: input.private,
      }),
    remove: (annotationId: string) =>
      this.send<void>("DELETE", `/api/v1/annotations/${id(annotationId)}`),
  };

  readonly connections = {
    list: () => this.get<ConnectionView[]>("/api/v1/connections"),
    create: (adapter: string, name: string) =>
      this.post<{
        id: string;
        name: string;
        adapter: string;
        createdAt: string;
      }>("/api/v1/connections", { adapter, name }),
    status: (id: string) =>
      this.get<ConnectionStatus>(
        `/api/v1/connections/${encodeURIComponent(id)}/status`,
      ),
    pause: (id: string) =>
      this.post<ConnectionStatus>(
        `/api/v1/connections/${encodeURIComponent(id)}/pause`,
      ),
    reactivate: (id: string) =>
      this.post<ConnectionStatus>(
        `/api/v1/connections/${encodeURIComponent(id)}/reactivate`,
      ),
    credentials: (id: string) =>
      this.get<CredentialsState>(
        `/api/v1/connections/${encodeURIComponent(id)}/credentials`,
      ),
    /** The vendor's sign-in page for the connection; the browser goes there and comes back to the connection. */
    signIn: (id: string) =>
      this.post<{ url: string }>(
        `/api/v1/connections/${encodeURIComponent(id)}/sign-in`,
        {},
      ),
    account: (id: string) =>
      this.get<AccountState>(
        `/api/v1/connections/${encodeURIComponent(id)}/account`,
      ),
    credentialOptions: (id: string, field: string) =>
      this.get<CredentialOption[]>(
        `/api/v1/connections/${encodeURIComponent(id)}/credential-options/${encodeURIComponent(field)}`,
      ),
    saveCredentials: (id: string, values: Record<string, string>) =>
      this.send<CredentialsState>(
        "PUT",
        `/api/v1/connections/${encodeURIComponent(id)}/credentials`,
        values,
      ),
    discovered: (id: string) =>
      this.get<DiscoveredDevice[]>(
        `/api/v1/connections/${encodeURIComponent(id)}/discovered`,
      ),
    bind: (
      id: string,
      sourceDeviceId: string,
      templateId: string,
      name: string,
    ) =>
      this.send<{ deviceId: string }>(
        "PUT",
        `/api/v1/connections/${encodeURIComponent(id)}/bindings/${encodeURIComponent(sourceDeviceId)}`,
        { templateId, name },
      ),
    /** Binds the given channels (all suggested, unbound ones when omitted) with their suggested templates. */
    bindSuggested: (connectionId: string, sourceDeviceIds?: string[]) =>
      this.post<DeviceBinding[]>(
        `/api/v1/connections/${id(connectionId)}/bindings`,
        { sourceDeviceIds: sourceDeviceIds ?? null },
      ),
    unbind: (connectionId: string, sourceDeviceId: string) =>
      this.send<void>(
        "DELETE",
        `/api/v1/connections/${id(connectionId)}/bindings/${id(sourceDeviceId)}`,
      ),
    rename: (connectionId: string, name: string) =>
      this.send<{ id: string; name: string }>(
        "PATCH",
        `/api/v1/connections/${id(connectionId)}`,
        { name },
      ),
    remove: (connectionId: string) =>
      this.send<void>("DELETE", `/api/v1/connections/${id(connectionId)}`),
  };

  /** Drops a cached resource, e.g. after an `invalidate` message from the live hub. */
  invalidate(path: string): void {
    this.cache.delete(path);
  }

  /** Forgets everything cached, e.g. after signing out, so nothing leaks into the next session. */
  clear(): void {
    this.cache.clear();
  }

  async get<T>(path: string): Promise<T> {
    const pending = this.inFlight.get(path);
    if (pending) {
      return pending as Promise<T>;
    }
    const request = this.fetchGet<T>(path).finally(() =>
      this.inFlight.delete(path),
    );
    this.inFlight.set(path, request);
    return request;
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.send<T>("POST", path, body);
  }

  async send<T>(
    method: "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    body?: unknown,
  ): Promise<T> {
    const response = await this.fetcher(this.baseUrl + path, {
      method,
      credentials: "same-origin",
      headers:
        body === undefined
          ? { ...CLIENT_HEADER }
          : { ...CLIENT_HEADER, "Content-Type": "application/json" },
      body: body === undefined ? null : JSON.stringify(body),
    });
    if (!response.ok) {
      throw await this.toError(response);
    }
    // 204 from deletes and unbinds.
    return (response.status === 204 ? undefined : await response.json()) as T;
  }

  private async fetchGet<T>(path: string): Promise<T> {
    const cached = this.cache.get(path);
    const response = await this.fetcher(this.baseUrl + path, {
      credentials: "same-origin",
      headers: cached
        ? { ...CLIENT_HEADER, "If-None-Match": cached.etag }
        : { ...CLIENT_HEADER },
    });
    if (response.status === 304 && cached) {
      return cached.data as T;
    }
    if (!response.ok) {
      throw await this.toError(response);
    }
    const data = (await response.json()) as T;
    const etag = response.headers.get("ETag");
    if (etag) {
      this.cache.set(path, { etag, data });
    }
    return data;
  }

  private async toError(response: Response): Promise<ApiError> {
    if (response.status === 401) {
      this.onUnauthorized?.();
      return new ApiError(401, [{ code: "AUTH_REQUIRED", params: {} }]);
    }
    try {
      const problem = (await response.json()) as { errors?: ApiErrorItem[] };
      if (Array.isArray(problem.errors) && problem.errors.length > 0) {
        return new ApiError(response.status, problem.errors);
      }
    } catch {
      // Not a problem+json body.
    }
    const code =
      response.status === 403 ? "FORBIDDEN" : `HTTP_${response.status}`;
    return new ApiError(response.status, [{ code, params: {} }]);
  }
}

function query(params: Record<string, string>): string {
  return new URLSearchParams(params).toString();
}
